import React from "react";

import {
  formatDateTimeFromIso,
  formatDateToDDMMYY,
  getCalendarDaysElapsedSince,
} from "../../utils/formatDate";

import { formatTime } from "../../utils/formatTime";

import {
  getCurrentRoundTarget,
  getGoalBatchesCompleted,
  getGoalRoundDisplayBatchSize,
  getTotalCounted,
} from "../../utils/counterState";

export default function CounterDetails({ counter }) {
  if (!counter) return null;

  // -------------------------
  // Derived values
  // -------------------------
  const totalCount = getTotalCounted(counter);
  const progressPercent =
    Math.floor((totalCount / counter.limit) * 100) || 0;

  const createdOnDisplay = formatDateToDDMMYY(counter.createdAt);
  const spentCalendarDays = getCalendarDaysElapsedSince(
    counter.createdAt
  );

  const isGoal = counter.type === "goal";

  const goalThisRoundTarget = isGoal
    ? getCurrentRoundTarget(counter)
    : 0;

  const goalDisplayBatch = isGoal
    ? getGoalRoundDisplayBatchSize(counter)
    : 0;

  const goalBatchesDone = isGoal
    ? getGoalBatchesCompleted(counter)
    : counter.rounds;

  const progressWidth = `${Math.min(
    100,
    Math.max(0, progressPercent)
  )}%`;

  // -------------------------
  // Reusable row component
  // -------------------------
  const Row = ({ label, value }) => (
    <p>
      <span className="counterDetails-label">{label} : </span>
      <span className="counterDetails-value">{value}</span>
    </p>
  );

  // -------------------------
  // Render
  // -------------------------
  return (
    <>
      {/* Common Fields */}
      <Row label="Current Count" value={counter.currentCount} />
      <Row label="Completed Count" value={totalCount} />

      {isGoal ? (
        <>
          <Row label="Batches completed" value={goalBatchesDone} />
          <Row label="Limit" value={counter.limit} />
        </>
      ) : (
        <>
          <Row label="Round" value={counter.rounds} />
          <Row label="Daily Target" value={counter.limit} />
        </>
      )}

      <Row
        label="Created on"
        value={createdOnDisplay || "—"}
      />

      <Row
        label="Time taken"
        value={formatTime(counter.totalSpentTime || 0)}
      />

      <Row
        label="Spent days"
        value={spentCalendarDays}
      />

      {isGoal && (
        <>
          <Row label="Total Days" value={counter.days} />

          <Row
            label="Counts to finish this round"
            value={
              goalThisRoundTarget > 0
                ? goalThisRoundTarget
                : counter.perDayCount
            }
          />

          <Row
            label="Each round size (limit ÷ days)"
            value={
              goalDisplayBatch > 0
                ? goalDisplayBatch
                : counter.perDayCount
            }
          />
        </>
      )}

      {/* Last Selected */}
      <p className="counterCard__lastSelected">
        <span className="counterCard__lastSelectedLabel">
          Last selected
        </span>
        <span className="counterCard__lastSelectedValue">
          {formatDateTimeFromIso(counter.lastSelectedAt)}
        </span>
      </p>

      {/* Progress */}
      <div
        className="counterDetails-progress fullWidth"
        role="group"
        aria-label="Progress"
      >
        <div className="counterDetails-progress__row">
          <span className="counterDetails-progress__label">
            Progress
          </span>
          <span className="counterDetails-progress__value">
            {progressPercent}%
          </span>
        </div>

        <div className="counterDetails-progress__track">
          <div
            className={`counterDetails-progress__fill ${
              progressPercent >= 100 ? "is-over" : ""
            }`}
            style={{ width: progressWidth }}
          />
        </div>
      </div>
    </>
  );
}